import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  VStack,
  Text,
  Button,
  Skeleton,
  SkeletonText,
  SimpleGrid,
  HStack,
  useDisclosure,
} from "@chakra-ui/react";
import Link from "next/link";
import { useTranslations } from "next-intl";

import SectionContainer from "components/layout/SectionContainer";
import { useUser } from "lib/contexts/useUser";
import useTranslationConstants from "lib/utils/constants";
import { getPaths } from "lib/utils/paths";
import type { SubscriptionPlansTranslations } from "src/contexts/LocalesContext";
import { useLocales } from "src/contexts/LocalesContext";

import type { ICoachesPageSectionProps, IPlanCardProps } from "./interfaces";
import SubscribeNewPlanModal from "./SubscribeNewPlanModal";
import type { ISubscriptionPlanByPkQuery } from "lib/api/sdk";
import { useSubscriptionPlanByPkQuery } from "lib/api/sdk";

const PlanCard = ({ plan, loading, coachId, onSubscribe }: IPlanCardProps) => {
  const { loggedIn } = useUser();
  const { getTranslations } = useLocales();
  const t = useTranslations("pages.coach");
  const { getSubscriptionPlanName } = useTranslationConstants();

  const translatedContent = getTranslations<SubscriptionPlansTranslations>(plan?.subscription_plan_translations as SubscriptionPlansTranslations[]);

  const spName = plan?.level ? getSubscriptionPlanName(plan?.level) : "";

  const registerLink = getPaths.register.athlete.stepAndParams(
    1,
    coachId,
    plan?.sport?.id,
    plan?.id,
  );

  return (
    <VStack
      w="full"
      bg="lightestGray"
      position="relative"
      mb={[0, 0, 6, 0]}
    >
      <VStack w="full" h="full" p={8} spacing={4} align="stretch">
        <HStack justify="space-evenly">
          <VStack spacing={0}>
            <Skeleton isLoaded={!loading}>
              <Text
                fontSize="4xl"
                fontWeight="bold"
                color="brandOrange.500"
                textAlign="center"
              >
                {plan?.weekly_price}€
              </Text>
            </Skeleton>
            <Skeleton isLoaded={!loading}>
              <Text
                textAlign="center"
                fontWeight="bold"
                fontSize="xs"
              >
                {t("perWeek")}
              </Text>
            </Skeleton>
          </VStack>
          <VStack spacing={0}>
            <Skeleton isLoaded={!loading}>
              <Text
                fontSize="4xl"
                fontWeight="bold"
                color="brandOrange.500"
                textAlign="center"
              >
                {Number(plan?.weekly_price) * 4}€
              </Text>
            </Skeleton>
            <Skeleton isLoaded={!loading}>
              <Text
                textAlign="center"
                fontWeight="bold"
                fontSize="xs"
              >
                {t("per4Weeks")}
              </Text>
            </Skeleton>
          </VStack>
        </HStack>

        <VStack w="full" h="full" align="stretch" spacing={1} mt="3">
          <Skeleton isLoaded={!loading}>
            <Heading size="md" textAlign="center">
              {spName}
            </Heading>
          </Skeleton>
          <SkeletonText textAlign="center" isLoaded={!loading}>
            <Text textAlign="center">
              {translatedContent?.description}
            </Text>
          </SkeletonText>
        </VStack>

        <VStack
          w="full"
          h="full"
          align="stretch"
          spacing={0}
          mb="4"
        >
          <Skeleton isLoaded={!loading}>
            <Text textAlign="center" fontSize="sm">
              <strong>{t("minDuration")}</strong> {plan?.min_duration} {t("weeks")}
            </Text>
          </Skeleton>
          <Skeleton isLoaded={!loading}>
            <Text textAlign="center" fontSize="sm">
              <strong>{t("recommendedDuration")}</strong> {plan?.recomended_duration}{" "}
              {t("weeks")}
            </Text>
          </Skeleton>
        </VStack>
      </VStack>

      <Box position="absolute" bottom="-20px">
        {loggedIn ? (
          <Button
            px="16"
            colorScheme="brandOrange"
            onClick={onSubscribe && !!plan
              ? () => onSubscribe(plan)
              : undefined
            }
          >
            {t("subscribe")}
          </Button>
        ) : (
          <Link passHref href={registerLink}>
            <Button
              px="16"
              colorScheme="brandOrange"
            >
              {t("start")}
            </Button>
          </Link>
        )}
      </Box>
    </VStack>
  );
};

const PlansSection = ({ data, loading }: ICoachesPageSectionProps) => {
  const t = useTranslations("pages.coach");
  const { getTranslations } = useLocales();
  const [selectedPlan, setSelectedPlan] = useState<ISubscriptionPlanByPkQuery["subscription_plan_by_pk"] | undefined>(undefined);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const SubscriptionPlanReq = useSubscriptionPlanByPkQuery({
    variables: { id: Number(selectedPlan) },
  });

  useEffect(() => {
    if (selectedPlan) SubscriptionPlanReq.refetch({
      id: selectedPlan.id,
    });
  }, [selectedPlan]);

  const hasValidPlans = (data?.coach_by_pk?.sports?.map(cs => cs.sport)
    .filter(sport => sport.subscription_plans?.some(plan => plan.coach_id === data?.coach_by_pk?.id)) || [])
    .length > 0;

  const setPlanAndOpenModal = (plan: ISubscriptionPlanByPkQuery["subscription_plan_by_pk"]) => {
    setSelectedPlan(plan);
    onOpen();
  };

  return !hasValidPlans ? null : (
    <>
      <Box id="planos-de-treino" w="full">
        <SectionContainer h="full" pt={0}>
          <VStack spacing={2}>
            <Text
              color="brandOrange.500"
              fontSize={["2xl", "3xl", "4xl", "5xl"]}
              fontWeight="bold"
              textTransform="uppercase"
            >
              {t("trainingPlans")}
            </Text>
            {loading
              ? [0, 1, 2].map(i => <PlanCard loading key={i} />)
              : data?.coach_by_pk?.sports?.map(cs => cs.sport)
                .filter(sport => sport.subscription_plans?.some(
                  plan => plan.coach_id === data?.coach_by_pk?.id
                )).map(sport => {
                  const sportTranslations = getTranslations(sport.sport_translations);
                  const plansToRender = sport.subscription_plans?.length
                    ? sport.subscription_plans
                      .filter(plan => plan.coach_id === data?.coach_by_pk?.id)
                      .sort((a, b) => (a.level || 0) - (b.level || 0))
                    : [];
                  return (
                    <Box key={sport.id} w="full">
                      <Text
                        textAlign="center"
                        fontSize={["2xl", "2xl", "3xl", "4xl"]}
                        fontWeight="bold"
                        textTransform="uppercase"
                      >
                        {sportTranslations?.title}
                      </Text>
                      <SimpleGrid
                        w="full"
                        columns={[1, 1, 2, 3]}
                        spacing={[12, 12, 6, 6]}
                        pt={12}
                        mb={16}
                      >
                        {plansToRender.map(plan => (
                          <PlanCard
                            key={plan.id}
                            plan={plan}
                            coachId={plan.coach_id}
                            onSubscribe={setPlanAndOpenModal}
                          />
                        ))}
                      </SimpleGrid>
                    </Box>
                  );
                })}
          </VStack>
        </SectionContainer>
      </Box>
      {SubscriptionPlanReq.data?.subscription_plan_by_pk &&
        <SubscribeNewPlanModal
          isOpen={isOpen}
          onClose={() => {
            setSelectedPlan(undefined);
            onClose();
          }}
          plan={SubscriptionPlanReq.data?.subscription_plan_by_pk}
        />
      }
    </>
  );
};

export default PlansSection;
